import { useId } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import Icon from '@/components/Icon/Icon';
import Input from '@/components/Input/Input';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import type { FamilyPersonalInfoListItem } from '@/features/section-attribute/types';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

type Props = CFormItemProps & {
  value?: FamilyPersonalInfoListItem[];
  onChange: (value: FamilyPersonalInfoListItem[]) => void;
  /** フォームが設置されている人の ID（遷移先 URL の構築に使用） */
  personId?: string;
};

function buildFullName(member: FamilyPersonalInfoListItem): string {
  return [member.lastName, member.middleName, member.firstName]
    .filter(Boolean)
    .join(' ');
}

function FamilyMemberReadCard({
  member,
  personId,
}: {
  member: FamilyPersonalInfoListItem;
  personId?: string;
}) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.familyPersonalInfoItem',
  });

  const fullName = buildFullName(member);

  return (
    <VStack className="gap-2">
      <Icon
        iconName="UserRoundIcon"
        text={member.relationship || '-'}
        colorVariant="neutral"
      />
      <Text variant="labelLarge" className="text-neutral-on-fill">
        {fullName || '-'}
      </Text>
      {personId && member.id && (
        <div className="self-end">
          <ActionButton
            variant="ghost"
            iconColor="muted"
            className="text-neutral-muted-on-fill"
            onClick={() =>
              window.open(`/person/${personId}/family/${member.id}`, '_blank')
            }
          >
            {t('viewProfile')}
          </ActionButton>
        </div>
      )}
    </VStack>
  );
}

export const CFormFamilyPersonalInfoListItem = buildCFormItem(
  (props: Props) => {
    const tc = useCommonTranslation();
    const { t } = useTranslation(defaultNS, {
      keyPrefix: 'features.cForm.familyPersonalInfoItem',
    });
    const idPrefix = useId();
    const values = props.value ?? [];

    const handleAdd = () => {
      props.onChange([
        ...values,
        {
          id: null,
          relationship: '',
          firstName: '',
          lastName: '',
          middleName: '',
        },
      ]);
    };

    const handleRemove = (index: number) => {
      props.onChange(values.filter((_, i) => i !== index));
    };

    const updateMember = (
      index: number,
      patch: Partial<FamilyPersonalInfoListItem>,
    ) => {
      const next = [...values];
      next[index] = { ...next[index], ...patch };
      props.onChange(next);
    };

    const renderReadMode = () => {
      if (values.length === 0) {
        return (
          <Text variant="pSmall" className="text-neutral-muted-on-fill">
            -
          </Text>
        );
      }

      return (
        <VStack className="gap-4 w-full">
          {values.map((member, index) => (
            <FamilyMemberReadCard
              key={`${idPrefix}-read-${index}`}
              member={member}
              personId={props.personId}
            />
          ))}
        </VStack>
      );
    };

    const renderWriteMode = () => {
      return (
        <VStack className="gap-4 w-full">
          {values.map((member, idx) => (
            <VStack
              key={`${idPrefix}-write-${idx}`}
              className="gap-3 border-l-2 border-neutral-muted-border pl-4 py-2 w-full"
            >
              <HStack className="justify-between items-center">
                <Text variant="label" className="text-neutral-muted-on-fill">
                  {member.relationship || t('familyMember')}
                </Text>
                {!props.readonly && (
                  <ActionButton
                    icon="Trash2Icon"
                    variant="ghost"
                    iconColor="muted"
                    onClick={() => handleRemove(idx)}
                    aria-label={tc('action.delete')}
                  />
                )}
              </HStack>

              <VStack className="gap-1">
                <Text
                  variant="labelSmall"
                  className="text-neutral-muted-on-fill"
                >
                  {t('relationship')}
                </Text>
                <Input
                  value={member.relationship ?? ''}
                  onChange={(e) =>
                    updateMember(idx, { relationship: e.target.value })
                  }
                  disabled={props.readonly}
                />
              </VStack>

              <HStack className="gap-2">
                <VStack className="flex-1 gap-1">
                  <Text
                    variant="labelSmall"
                    className="text-neutral-muted-on-fill"
                  >
                    {t('lastName')}
                  </Text>
                  <Input
                    value={member.lastName ?? ''}
                    onChange={(e) =>
                      updateMember(idx, { lastName: e.target.value })
                    }
                    disabled={props.readonly}
                  />
                </VStack>
                <VStack className="flex-1 gap-1">
                  <Text
                    variant="labelSmall"
                    className="text-neutral-muted-on-fill"
                  >
                    {t('firstName')}
                  </Text>
                  <Input
                    value={member.firstName ?? ''}
                    onChange={(e) =>
                      updateMember(idx, { firstName: e.target.value })
                    }
                    disabled={props.readonly}
                  />
                </VStack>
              </HStack>

              <VStack className="gap-1">
                <Text
                  variant="labelSmall"
                  className="text-neutral-muted-on-fill"
                >
                  {t('middleName')}
                </Text>
                <Input
                  value={member.middleName ?? ''}
                  onChange={(e) =>
                    updateMember(idx, { middleName: e.target.value })
                  }
                  disabled={props.readonly}
                />
              </VStack>
            </VStack>
          ))}

          {!props.readonly && (
            <ActionButton
              className="pl-0 self-start"
              icon="PlusIcon"
              onClick={handleAdd}
              variant="ghost"
              iconColor="primary"
            >
              {tc('action.add')}
            </ActionButton>
          )}
        </VStack>
      );
    };

    return (
      <CFormItemBase
        {...props}
        readComponent={renderReadMode()}
        writeComponent={renderWriteMode()}
      />
    );
  },
);
